import type { DocsSection } from "@/presentation/data/docs/docs-content.types";

export const buildDeploySection: DocsSection = {
  label: "Build & Deploy",
  pages: [
    {
      slug: "image-build",
      title: "Image build",
      description: "apps.json and the workspace manifest pin.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "The ERPNext image is the official ",
            { code: "frappe/erpnext" },
            " plus ",
            { code: "yango-customizations" },
            " at a pinned tag, composed via ",
            { code: "apps.json" },
            ". The pin lives in the workspace manifest, which is the compatibility source of truth.",
          ],
        },
        {
          kind: "heading",
          id: "release-flow",
          text: "Shipping a customization release",
        },
        {
          kind: "list",
          ordered: true,
          items: [
            [
              "Land the customization in ",
              { code: "yango-customizations" },
              " and tag it (",
              { code: "yango-customizations/<ver>" },
              ").",
            ],
            ["Bump the pin in ", { code: "workspace/workspace.repos.yaml" }, " (in yango-app)."],
            ["Run the ERPNext deploy workflow (see CI/CD)."],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Product-first ordering",
          text: "Product logic lands in the product repo + release tag first; then the manifest pin bumps in yango-app. A cross-repo change is two coordinated changes.",
        },
      ],
    },
    {
      slug: "ci-cd",
      title: "CI/CD",
      description: "The deploy workflows and how to ship a release.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "ERPNext deploys via ",
            { code: ".github/workflows/deploy-erpnext.yml" },
            " — ",
            { strong: "manual" },
            " (",
            { code: "workflow_dispatch" },
            "). The pipeline:",
          ],
        },
        {
          kind: "list",
          ordered: true,
          items: [
            [
              "Build the image ONCE (AWS-free), ",
              { code: "frappe/erpnext" },
              " + ",
              { code: "yango-customizations@<pinned-tag>" },
              " via apps.json, tagged ",
              { code: "git-<sha>" },
              " — publish to GHCR by digest.",
            ],
            [
              "The env job pulls that exact digest and promotes the identical image to this account's ECR.",
            ],
            [
              "Migrate hard gate: ",
              { code: "bench --site all migrate" },
              " (runs the after_migrate setters) — aborts the deploy on a non-zero exit, before any traffic shifts.",
            ],
            [
              { code: "roll-services.sh" },
              " registers a new task-def revision per service at ",
              { code: ":git-<sha>" },
              " and rolls all six ERPNext services.",
            ],
          ],
        },
        {
          kind: "callout",
          tone: "success",
          title: "Migrate gate runs the freshly-built image (BF-177)",
          text: [
            "The gate no longer runs the migrate task family's baked-in bootstrap tag. It describes the migrate task-def, swaps ",
            { strong: "only" },
            " the ",
            { code: "migrate" },
            " container's image to this deploy's ECR ",
            { code: ":git-<sha>" },
            ", registers a NEW revision, and runs THAT — the same bytes the fleet then rolls to. It fails fast if the family has no single container named ",
            { code: "migrate" },
            " (so a rename can never silently reintroduce the stale-image bug).",
          ],
        },
        {
          kind: "heading",
          id: "image-tag-reconciliation",
          text: "Image-tag reconciliation (SSM)",
        },
        {
          kind: "paragraph",
          text: [
            "CI owns the deployed image tag; Terraform reads it. After a successful roll, ",
            { code: "roll-services.sh" },
            " writes the tag to the SSM param ",
            { code: "/yango/<env>/erpnext/image_tag" },
            " (the assessment deploy does the same at ",
            { code: "/yango/<env>/assessment/image_tag" },
            "). Terraform reads that param for the service image, so a later ",
            { code: "terraform apply" },
            " never rolls the fleet back to a stale pinned tag (BF-184, BF-190).",
          ],
        },
        {
          kind: "list",
          ordered: false,
          items: [
            [
              { code: "image_tag_bootstrap" },
              " seeds the SSM param on the FIRST apply (set to the env's current live tag), then ",
              { code: "ignore_changes" },
              " hands ownership to CI — Terraform never fights the deployed tag again.",
            ],
            [
              { code: "-var erpnext_image_tag=<tag>" },
              " (or ",
              { code: "assessment_image_tag" },
              ") WINS over the SSM value — the deliberate lever for a rollback pin.",
            ],
          ],
        },
        {
          kind: "heading",
          id: "assessment-deploy",
          text: "Deploying the assessment app",
        },
        {
          kind: "paragraph",
          text: [
            "The assessment SPA is built + deployed by ",
            { code: ".github/workflows/deploy-assessment.yml" },
            " — ",
            { strong: "manual" },
            " (",
            { code: "workflow_dispatch" },
            ") only. The pipeline:",
          ],
        },
        {
          kind: "list",
          ordered: true,
          items: [
            [
              "Builds the SPA in the env-scoped ",
              { code: "dev" },
              " job (authenticating to AWS via OIDC).",
            ],
            [
              "Pushes an immutable ",
              { code: "git-<sha>" },
              " image to ECR (",
              { code: "yango/dev/assessment" },
              ").",
            ],
            [
              { code: "roll-assessment.sh" },
              " registers a new task-def revision and rolls the ",
              { code: "assessment-web" },
              " service.",
            ],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Public build-args",
          text: [
            "The ",
            { code: "VITE_*" },
            " build-args are public (baked into the SPA bundle) and come from the dev GitHub Environment vars.",
          ],
        },
      ],
    },
  ],
};
